import axiosInstance from './axiosInstance'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RetentionCustomer {
  id: string
  outletId: string
  phoneNumber: string
  customerName: string
  mobile: string | null
  email: string | null
  recencyDays: number
  lastOrderDate: string | null
  // 'Active' = currently in retention API (not yet ordered)
  // 'Converted' = disappeared from API (assumed ordered) — history preserved
  status: 'Active' | 'Converted'
  convertedAt: string | null
  orderId?: string | null
  orderValue?: string | null
  orderedByUserId?: string | null
  orderedByName?: string | null
  outletName?: string | null
  orderedAt?: string | null
  customerTypeId?: number | null
  customerType?: string | null
  warehouseId?: number | null
  warehouseName?: string | null
  orgId: string | null
  lobId?: string | null
  ownerId: string | null
  ownerName: string | null
  assignedAt?: string | null     // <--- ADD THIS
  isConnected?: boolean          // <--- ADD THIS
  helpAssigneeId?: string | null
  helpAssigneeName?: string | null
  helpAssigneeEmail?: string | null
  createdAt: string
  updatedAt: string
}

export interface RetentionActivity {
  id: string
  retentionCustomerId: string
  type: string
  title: string
  description: string | null
  performedBy: string
  performedByName: string
  metadataJson?: string
  metadata?: any
  createdAt: string
}

export interface RetentionNote {
  id: string
  retentionCustomerId: string
  content: string
  createdBy: { id: string; name: string; email: string; avatarUrl: string | null } | null
  createdAt: string
  updatedAt: string
}

export interface RetentionTask {
  id: string
  retentionCustomerId: string
  title: string
  description: string | null
  dueDate: string | null
  status: 'OPEN' | 'IN_PROGRESS' | 'DONE'
  priority: 'LOW' | 'MEDIUM' | 'HIGH'
  assignedToId: string | null
  assignedToName: string | null
  assignedToAvatar: string | null
  createdById: string | null
  createdByName: string | null
  completedAt: string | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export interface RetentionFollowUp {
  id: string
  retentionCustomerId: string
  title: string | null
  type: string
  scheduledAt: string
  endTime: string | null
  notes: string | null
  reminderMinutes: number | null
  status: string
  outcome: string | null
  assignedTo: { id: string; name: string; email: string; avatarUrl: string | null } | null
  createdBy: { id: string; name: string; email: string; avatarUrl: string | null } | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface RetentionAttachment {
  id: string
  retentionCustomerId: string
  fileName: string
  url: string
  contentType: string
  sizeBytes: number
  uploadedById: string | null
  uploadedByName: string | null
  uploadedByAvatar: string | null
  createdAt: string
}

export interface RetentionCustomerUpsertPayload {
  outletId: string
  customerName: string
  mobile?: string | null
  email?: string | null
  recencyDays: number
  lastOrderDate?: string | null
  orgId?: string | null
  warehouseId?: number | null
  warehouseName?: string | null
}

export interface BulkAssignRetentionCustomerItem {
  outletId: string
  customerName: string
  mobile: string | null
  email: string | null
  recencyDays: number
  lastOrderDate: string | null
  customerTypeId?: number | null
  customerType?: string | null
  warehouseId?: number | null
  warehouseName?: string | null
}

export interface CreateRetentionTaskPayload {
  title: string
  description?: string
  dueDate?: string
  priority?: string
  assignedToId?: string
}

export interface UpdateRetentionTaskPayload {
  title?: string
  description?: string | null
  dueDate?: string | null
  status?: string
  priority?: string
  assignedToId?: string | null
}

export interface CreateRetentionFollowUpPayload {
  title?: string
  type: string
  scheduledAt: string
  endTime?: string
  notes?: string
  reminderMinutes?: number
  assignedToId?: string
}

// ── API ───────────────────────────────────────────────────────────────────────

export const retentionApi = {
  // ── Retention Customer CRUD ───────────────────────────────────────────────

  upsert: async (payload: RetentionCustomerUpsertPayload): Promise<RetentionCustomer> => {
    const res = await axiosInstance.post<{ data: RetentionCustomer }>('/retention-customers/upsert', payload)
    return res.data.data
  },

  getById: async (id: string): Promise<RetentionCustomer> => {
    const res = await axiosInstance.get<{ data: RetentionCustomer }>(`/retention-customers/${id}`)
    return res.data.data
  },

  getAll: async (orgId?: string | null, ownerId?: string | null): Promise<RetentionCustomer[]> => {
    const res = await axiosInstance.get<{ data: RetentionCustomer[] }>('/retention-customers', {
      params: { orgId, ownerId }
    })
    return res.data.data
  },

  getByOutletIds: async (outletIds: string[]): Promise<RetentionCustomer[]> => {
    if (!outletIds.length) return []
    const res = await axiosInstance.post<{ data: RetentionCustomer[] }>('/retention-customers/by-outlets', outletIds)
    return res.data.data ?? []
  },

  getByOwnerIds: async (orgId: string | null | undefined, ownerIds: string[]): Promise<RetentionCustomer[]> => {
    if (!ownerIds.length) return []
    const res = await axiosInstance.get<{ data: RetentionCustomer[] }>('/retention-customers', {
      params: { orgId, ownerIds: ownerIds.join(',') }
    })
    return res.data.data ?? []
  },

  getPagedByOwnerIds: async (params: {
    orgId?: string | null
    ownerIds?: string[]
    ownerId?: string
    search?: string
    customerType?: string
    customerTypeId?: number
    warehouseName?: string
    warehouseId?: number
    page?: number
    pageSize?: number
    isConnected?: boolean
  }): Promise<{ items: RetentionCustomer[]; total: number; page: number; pageSize: number; totalPages: number }> => {
    const { ownerIds, ...rest } = params
    const res = await axiosInstance.get<{
      data: { items: RetentionCustomer[]; total: number; page: number; pageSize: number; totalPages: number }
    }>('/retention-customers/paged', {
      params: {
        ...rest,
        ownerIds: ownerIds?.length ? ownerIds.join(',') : undefined,
      }
    })
    return res.data.data
  },

  bulkAssign: async (customers: BulkAssignRetentionCustomerItem[], ownerId: string): Promise<void> => {
    await axiosInstance.patch('/retention-customers/bulk/assign', { customers, ownerId })
  },

  // Mark as Converted (assumed order placed — disappears from AI API).
  // History is fully preserved. Never deletes anything.
  convertById: async (id: string): Promise<RetentionCustomer> => {
    const res = await axiosInstance.patch<{ data: RetentionCustomer }>(`/retention-customers/${id}/convert`)
    return res.data.data
  },

  convertByOutletId: async (outletId: string, phone?: string | null): Promise<RetentionCustomer | null> => {
    try {
      const params = phone ? `?phone=${encodeURIComponent(phone)}` : ''
      const res = await axiosInstance.patch<{ data: RetentionCustomer }>(`/retention-customers/by-outlet/${encodeURIComponent(outletId)}/convert${params}`)
      return res.data.data
    } catch {
      return null
    }
  },

  getByOutletId: async (outletId: string, phone?: string | null): Promise<RetentionCustomer | null> => {
    try {
      const params = phone ? `?phone=${encodeURIComponent(phone)}` : ''
      const res = await axiosInstance.get<{ data: RetentionCustomer }>(`/retention-customers/by-outlet/${encodeURIComponent(outletId)}${params}`)
      return res.data.data
    } catch {
      return null
    }
  },

  // ── Activities ────────────────────────────────────────────────────────────

  getActivities: async (id: string): Promise<RetentionActivity[]> => {
    const res = await axiosInstance.get<{ data: RetentionActivity[] }>(`/retention-customers/${id}/activities`)
    return res.data.data
  },

  addActivity: async (id: string, payload: { type: string; title: string; description?: string | null }): Promise<RetentionActivity> => {
    const res = await axiosInstance.post<{ data: RetentionActivity }>(`/retention-customers/${id}/activities`, payload)
    return res.data.data
  },

  // ── Notes ─────────────────────────────────────────────────────────────────

  getNotes: async (id: string): Promise<RetentionNote[]> => {
    const res = await axiosInstance.get<{ data: RetentionNote[] }>(`/retention-customers/${id}/notes`)
    return res.data.data
  },

  addNote: async (id: string, content: string): Promise<RetentionNote[]> => {
    const res = await axiosInstance.post<{ data: RetentionNote[] }>(`/retention-customers/${id}/notes`, { content })
    return res.data.data
  },

  updateNote: async (id: string, noteId: string, content: string): Promise<RetentionNote[]> => {
    const res = await axiosInstance.put<{ data: RetentionNote[] }>(`/retention-customers/${id}/notes/${noteId}`, { content })
    return res.data.data
  },

  deleteNote: async (id: string, noteId: string): Promise<void> => {
    await axiosInstance.delete(`/retention-customers/${id}/notes/${noteId}`)
  },

  // ── Tasks ─────────────────────────────────────────────────────────────────

  getTasks: async (id: string): Promise<RetentionTask[]> => {
    const res = await axiosInstance.get<{ data: RetentionTask[] }>(`/retention-customers/${id}/tasks`)
    return res.data.data
  },

  addTask: async (id: string, payload: CreateRetentionTaskPayload): Promise<RetentionTask> => {
    const res = await axiosInstance.post<{ data: RetentionTask }>(`/retention-customers/${id}/tasks`, payload)
    return res.data.data
  },

  updateTask: async (id: string, taskId: string, payload: UpdateRetentionTaskPayload): Promise<RetentionTask> => {
    const res = await axiosInstance.put<{ data: RetentionTask }>(`/retention-customers/${id}/tasks/${taskId}`, payload)
    return res.data.data
  },

  deleteTask: async (id: string, taskId: string): Promise<void> => {
    await axiosInstance.delete(`/retention-customers/${id}/tasks/${taskId}`)
  },

  // Calls now live entirely in callLogsApi (getByRetention / startCallForRetention /
  // endCallForRetention / saveOutcomeForRetention) — same CallLogs table Leads/Deals/
  // Contacts use. The old getCalls/addCall here duplicated records; removed.

  // ── Follow-ups (Events) ───────────────────────────────────────────────────

  getFollowUps: async (id: string): Promise<RetentionFollowUp[]> => {
    const res = await axiosInstance.get<{ data: RetentionFollowUp[] }>(`/retention-customers/${id}/follow-ups`)
    return res.data.data
  },

  addFollowUp: async (id: string, payload: CreateRetentionFollowUpPayload): Promise<RetentionFollowUp> => {
    const res = await axiosInstance.post<{ data: RetentionFollowUp }>(`/retention-customers/${id}/follow-ups`, payload)
    return res.data.data
  },

  completeFollowUp: async (id: string, followUpId: string, outcome?: string): Promise<RetentionFollowUp> => {
    const res = await axiosInstance.patch<{ data: RetentionFollowUp }>(`/retention-customers/${id}/follow-ups/${followUpId}/complete`, { outcome })
    return res.data.data
  },

  cancelFollowUp: async (id: string, followUpId: string): Promise<RetentionFollowUp> => {
    const res = await axiosInstance.patch<{ data: RetentionFollowUp }>(`/retention-customers/${id}/follow-ups/${followUpId}/cancel`)
    return res.data.data
  },

  // ── Attachments ───────────────────────────────────────────────────────────

  getAttachments: async (id: string): Promise<RetentionAttachment[]> => {
    const res = await axiosInstance.get<{ data: RetentionAttachment[] }>(`/retention-customers/${id}/attachments`)
    return res.data.data
  },

  uploadAttachment: async (id: string, file: File, onProgress?: (pct: number) => void): Promise<RetentionAttachment> => {
    const form = new FormData()
    form.append('file', file)
    const res = await axiosInstance.post<{ data: RetentionAttachment }>(`/retention-customers/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total))
      },
    })
    return res.data.data
  },

  deleteAttachment: async (id: string, attachmentId: string): Promise<void> => {
    await axiosInstance.delete(`/retention-customers/${id}/attachments/${attachmentId}`)
  },

  getWithOrders: async (orgId?: string): Promise<RetentionCustomer[]> => {
    const res = await axiosInstance.get<{ data: RetentionCustomer[] }>('/retention-customers', {
      params: { hasOrder: true, orgId },
    })
    return res.data.data ?? []
  },

  getRecencyCustomerTypes: async (): Promise<string[]> => {
    try {
      sessionStorage.removeItem('crm_recency_customer_types')
    } catch {}
    const res = await axiosInstance.get<{ data: string[] }>('/retention-customers/customer-types')
    return res.data.data ?? []
  },

  exportRecencyOrders: async (params?: Record<string, any>): Promise<void> => {
    const cleanParams = { ...params }
    delete cleanParams.page
    delete cleanParams.pageSize

    const res = await axiosInstance.get('/retention-customers/recency-orders/export', {
      params: cleanParams,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }))
    const link = document.createElement('a')
    link.href = url
    const dateStr = new Date().toISOString().slice(0, 10)
    link.setAttribute('download', `Recency_Orders_${dateStr}.csv`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  getRecencyOrderStats: async (params?: Record<string, any>): Promise<{ total: number; totalOrderValue: number; totalCalls?: number }> => {
    const cleanParams = { ...params }
    delete cleanParams.page
    delete cleanParams.pageSize

    const res = await axiosInstance.get<{ data: { total: number; totalOrderValue: number; totalCalls?: number } }>(
      '/retention-customers/recency-orders/stats',
      { params: cleanParams }
    )
    return res.data.data
  },

  getRecencyOrders: async (params?: {
    search?: string
    dateTab?: string
    dateFrom?: string
    dateTo?: string
    customerType?: string
    warehouse?: string
    ownerId?: string
    orderedBy?: string
    assignedUser?: string
    page?: number
    pageSize?: number
  }): Promise<{ items: RetentionCustomer[]; total: number; totalOrderValue: number; page: number; pageSize: number; totalPages: number }> => {
    const res = await axiosInstance.get<{
      data: { items: RetentionCustomer[]; total: number; totalOrderValue: number; page: number; pageSize: number; totalPages: number }
    }>('/retention-customers/recency-orders', { params })
    return res.data.data
  },

  getGatewayOrderDetail: async (orderId: string): Promise<any> => {
    // const baseUrl = import.meta.env.VITE_DIT_GATEWAY_URL
    const baseUrl = import.meta.env.VITE_ORDER_DETAIL_SERVICE
    const res = await axiosInstance.get(`${baseUrl}/api/admin/order_detail`, {
      params: { id: orderId },
    })
    return res.data.data
  },

  getWarehouses: async (): Promise<Array<{ id: string; warehouseExternalId: number; warehouseName: string; warehouseAddress?: string }>> => {
    const res = await axiosInstance.get<{ data: Array<{ id: string; warehouseExternalId: number; warehouseName: string; warehouseAddress?: string }> }>('/ware-houses')
    return res.data.data
  },

  orderPlaced: async (payload: {
    outletId: string
    orderId: string
    userId: string
    outletName: string
    orderValue: string
    customerTypeId?: number | null
    customerType?: string | null
  }): Promise<RetentionCustomer> => {
    const res = await axiosInstance.post<{ data: RetentionCustomer }>('/retention-customers/order-placed', payload)
    return res.data.data
  },

  updateHelpAssignee: async (id: string, helpAssigneeId: string | null): Promise<RetentionCustomer> => {
    const res = await axiosInstance.patch<{ data: RetentionCustomer }>(`/retention-customers/${id}/help-assignee`, { helpAssigneeId })
    return res.data.data
  },
}
